/**
 * Notes de version affichées dans le jeu (popup « Nouveautés »).
 *
 * Une entrée par version PUBLIÉE, la plus récente en premier. Les textes vivent dans
 * `fr.json` / `en.json` — jamais ici (§10) : ce fichier ne connaît que les CLÉS, typées
 * `TranslationKey` en `import type` pour que le compilateur refuse une clé inconnue
 * sans créer de dépendance d'exécution vers `src/i18n` (même patron que
 * `BuildingDefinition.icon`).
 *
 * Règle de tenue : à chaque bump de version dans `package.json`, ajouter l'entrée ici
 * et ses clés dans les deux dictionnaires. `CHANGELOG.md` reste la référence complète ;
 * cette liste est le résumé que le joueur lit — trois à six lignes, parlées, sans
 * jargon.
 */

import type { TranslationKey } from '@/i18n';

export interface VersionNotes {
  /** Numéro SemVer, tel qu'affiché. Jamais traduit : un numéro se lit partout. */
  readonly version: string;
  readonly itemKeys: readonly TranslationKey[];
}

/** Version courante du bundle — injectée depuis `package.json` au build. */
export const APP_VERSION: string = __APP_VERSION__;

export const VERSION_NOTES: readonly VersionNotes[] = [
  {
    version: '1.15.0',
    itemKeys: ['changelog.v1_15_0.i0'],
  },
  {
    version: '1.14.0',
    itemKeys: ['changelog.v1_14_0.i0'],
  },
  {
    version: '1.13.0',
    itemKeys: ['changelog.v1_13_0.i0'],
  },
  {
    version: '1.12.0',
    itemKeys: ['changelog.v1_12_0.i0'],
  },
  {
    version: '1.11.0',
    itemKeys: ['changelog.v1_11_0.i0'],
  },
  {
    version: '1.10.0',
    itemKeys: ['changelog.v1_10_0.i0'],
  },
  {
    version: '1.9.0',
    itemKeys: ['changelog.v1_9_0.i0'],
  },
  {
    version: '1.8.0',
    itemKeys: ['changelog.v1_8_0.i0'],
  },
  {
    version: '1.7.0',
    itemKeys: ['changelog.v1_7_0.i0'],
  },
  {
    version: '1.6.0',
    itemKeys: ['changelog.v1_6_0.i0'],
  },
  {
    version: '1.5.0',
    itemKeys: ['changelog.v1_5_0.i0', 'changelog.v1_5_0.i1'],
  },
  {
    version: '1.4.0',
    itemKeys: ['changelog.v1_4_0.i0'],
  },
  {
    version: '1.3.0',
    itemKeys: ['changelog.v1_3_0.i0', 'changelog.v1_3_0.i1'],
  },
  {
    version: '1.2.0',
    itemKeys: ['changelog.v1_2_0.i0'],
  },
  {
    version: '1.1.0',
    itemKeys: ['changelog.v1_1_0.i0'],
  },
  {
    version: '1.0.0',
    itemKeys: ['changelog.v1_0_0.i0', 'changelog.v1_0_0.i1'],
  },
  {
    version: '0.99.1',
    itemKeys: ['changelog.v0_99_1.i0'],
  },
  {
    version: '0.99.0',
    itemKeys: ['changelog.v0_99_0.i0', 'changelog.v0_99_0.i1'],
  },
  {
    version: '0.98.0',
    itemKeys: ['changelog.v0_98_0.i0', 'changelog.v0_98_0.i1'],
  },
  {
    version: '0.97.0',
    itemKeys: ['changelog.v0_97_0.i0', 'changelog.v0_97_0.i1'],
  },
  {
    version: '0.96.1',
    itemKeys: ['changelog.v0_96_1.i0'],
  },
  {
    version: '0.96.0',
    itemKeys: ['changelog.v0_96_0.i0'],
  },
  {
    version: '0.95.0',
    itemKeys: ['changelog.v0_95_0.i0', 'changelog.v0_95_0.i1'],
  },
  {
    version: '0.94.1',
    itemKeys: ['changelog.v0_94_1.i0', 'changelog.v0_94_1.i1'],
  },
  {
    version: '0.94.0',
    itemKeys: ['changelog.v0_94_0.i0', 'changelog.v0_94_0.i1'],
  },
  {
    version: '0.93.0',
    itemKeys: ['changelog.v0_93_0.i0', 'changelog.v0_93_0.i1'],
  },
  {
    version: '0.92.1',
    itemKeys: ['changelog.v0_92_1.i0', 'changelog.v0_92_1.i1'],
  },
  {
    version: '0.92.0',
    itemKeys: ['changelog.v0_92_0.i0', 'changelog.v0_92_0.i1'],
  },
  {
    version: '0.91.0',
    itemKeys: ['changelog.v0_91_0.i0', 'changelog.v0_91_0.i1'],
  },
  {
    version: '0.90.2',
    itemKeys: ['changelog.v0_90_2.i0', 'changelog.v0_90_2.i1'],
  },
  {
    version: '0.90.1',
    itemKeys: ['changelog.v0_90_1.i0', 'changelog.v0_90_1.i1'],
  },
  {
    version: '0.90.0',
    itemKeys: ['changelog.v0_90_0.i0', 'changelog.v0_90_0.i1'],
  },
  {
    version: '0.89.1',
    itemKeys: ['changelog.v0_89_1.i0', 'changelog.v0_89_1.i1'],
  },
  {
    version: '0.89.0',
    itemKeys: ['changelog.v0_89_0.i0', 'changelog.v0_89_0.i1'],
  },
  {
    version: '0.88.0',
    itemKeys: ['changelog.v0_88_0.i0', 'changelog.v0_88_0.i1'],
  },
  {
    version: '0.87.0',
    itemKeys: ['changelog.v0_87_0.i0', 'changelog.v0_87_0.i1'],
  },
  {
    version: '0.86.0',
    itemKeys: ['changelog.v0_86_0.i0', 'changelog.v0_86_0.i1'],
  },
  {
    version: '0.85.1',
    itemKeys: ['changelog.v0_85_1.i0', 'changelog.v0_85_1.i1'],
  },
  {
    version: '0.85.0',
    itemKeys: ['changelog.v0_85_0.i0', 'changelog.v0_85_0.i1'],
  },
  {
    version: '0.84.2',
    itemKeys: ['changelog.v0_84_2.i0', 'changelog.v0_84_2.i1'],
  },
  {
    version: '0.84.1',
    itemKeys: ['changelog.v0_84_1.i0', 'changelog.v0_84_1.i1'],
  },
  {
    version: '0.84.0',
    itemKeys: ['changelog.v0_84_0.i0', 'changelog.v0_84_0.i1'],
  },
  {
    version: '0.83.0',
    itemKeys: ['changelog.v0_83_0.i0', 'changelog.v0_83_0.i1'],
  },
  {
    version: '0.82.0',
    itemKeys: ['changelog.v0_82_0.i0', 'changelog.v0_82_0.i1'],
  },
  {
    version: '0.81.1',
    itemKeys: ['changelog.v0_81_1.i0', 'changelog.v0_81_1.i1'],
  },
  {
    version: '0.81.0',
    itemKeys: ['changelog.v0_81_0.i0', 'changelog.v0_81_0.i1'],
  },
  {
    version: '0.80.0',
    itemKeys: ['changelog.v0_80_0.i0', 'changelog.v0_80_0.i1'],
  },
  {
    version: '0.79.0',
    itemKeys: ['changelog.v0_79_0.i0', 'changelog.v0_79_0.i1'],
  },
  {
    version: '0.78.0',
    itemKeys: ['changelog.v0_78_0.i0', 'changelog.v0_78_0.i1'],
  },
  {
    version: '0.77.0',
    itemKeys: ['changelog.v0_77_0.i0'],
  },
  {
    version: '0.76.0',
    itemKeys: ['changelog.v0_76_0.i0', 'changelog.v0_76_0.i1'],
  },
  {
    version: '0.75.0',
    itemKeys: ['changelog.v0_75_0.i0', 'changelog.v0_75_0.i1'],
  },
  {
    version: '0.74.0',
    itemKeys: ['changelog.v0_74_0.i0', 'changelog.v0_74_0.i1'],
  },
  {
    version: '0.73.0',
    itemKeys: ['changelog.v0_73_0.i0', 'changelog.v0_73_0.i1'],
  },
  {
    version: '0.72.1',
    itemKeys: ['changelog.v0_72_1.i0'],
  },
  {
    version: '0.72.0',
    itemKeys: ['changelog.v0_72_0.i0', 'changelog.v0_72_0.i1'],
  },
  {
    version: '0.71.0',
    itemKeys: ['changelog.v0_71_0.i0', 'changelog.v0_71_0.i1'],
  },
  {
    version: '0.70.0',
    itemKeys: ['changelog.v0_70_0.i0'],
  },
  {
    version: '0.69.0',
    itemKeys: ['changelog.v0_69_0.i0', 'changelog.v0_69_0.i1'],
  },
  {
    version: '0.68.0',
    itemKeys: ['changelog.v0_68_0.i0', 'changelog.v0_68_0.i1'],
  },
  {
    version: '0.67.1',
    itemKeys: ['changelog.v0_67_1.i0', 'changelog.v0_67_1.i1'],
  },
  {
    version: '0.67.0',
    itemKeys: ['changelog.v0_67_0.i0', 'changelog.v0_67_0.i1'],
  },
  {
    version: '0.66.0',
    itemKeys: ['changelog.v0_66_0.i0', 'changelog.v0_66_0.i1'],
  },
  {
    version: '0.65.0',
    itemKeys: ['changelog.v0_65_0.i0'],
  },
  {
    version: '0.64.0',
    itemKeys: ['changelog.v0_64_0.i0', 'changelog.v0_64_0.i1', 'changelog.v0_64_0.i2'],
  },
  {
    version: '0.63.2',
    itemKeys: ['changelog.v0_63_2.i0', 'changelog.v0_63_2.i1'],
  },
  {
    version: '0.63.1',
    itemKeys: ['changelog.v0_63_1.i0'],
  },
  {
    version: '0.63.0',
    itemKeys: ['changelog.v0_63_0.i0'],
  },
  {
    version: '0.62.0',
    itemKeys: ['changelog.v0_62_0.i0', 'changelog.v0_62_0.i1'],
  },
  {
    version: '0.61.0',
    itemKeys: ['changelog.v0_61_0.i0'],
  },
  {
    version: '0.60.0',
    itemKeys: ['changelog.v0_60_0.i0', 'changelog.v0_60_0.i1'],
  },
  {
    version: '0.59.0',
    itemKeys: ['changelog.v0_59_0.i0'],
  },
  {
    version: '0.58.0',
    itemKeys: ['changelog.v0_58_0.i0'],
  },
  {
    version: '0.57.1',
    itemKeys: ['changelog.v0_57_1.i0'],
  },
  {
    version: '0.57.0',
    itemKeys: ['changelog.v0_57_0.i0'],
  },
  {
    version: '0.56.0',
    itemKeys: ['changelog.v0_56_0.i0', 'changelog.v0_56_0.i1'],
  },
  {
    version: '0.55.1',
    itemKeys: ['changelog.v0_55_1.i0'],
  },
  {
    version: '0.55.0',
    itemKeys: ['changelog.v0_55_0.i0', 'changelog.v0_55_0.i1'],
  },
  {
    version: '0.54.6',
    itemKeys: ['changelog.v0_54_6.i0'],
  },
  {
    version: '0.54.5',
    itemKeys: ['changelog.v0_54_5.i0'],
  },
  {
    version: '0.54.4',
    itemKeys: ['changelog.v0_54_4.i0'],
  },
  {
    version: '0.54.3',
    itemKeys: ['changelog.v0_54_3.i0'],
  },
  {
    version: '0.54.2',
    itemKeys: ['changelog.v0_54_2.i0'],
  },
  {
    version: '0.54.1',
    itemKeys: ['changelog.v0_54_1.i0'],
  },
  {
    version: '0.54.0',
    itemKeys: ['changelog.v0_54_0.i0', 'changelog.v0_54_0.i1'],
  },
  {
    version: '0.53.1',
    itemKeys: ['changelog.v0_53_1.i0', 'changelog.v0_53_1.i1'],
  },
  {
    version: '0.53.0',
    itemKeys: [
      'changelog.v0_53_0.i0',
      'changelog.v0_53_0.i1',
      'changelog.v0_53_0.i2',
      'changelog.v0_53_0.i3',
    ],
  },
  {
    version: '0.52.0',
    itemKeys: ['changelog.v0_52_0.i0', 'changelog.v0_52_0.i1', 'changelog.v0_52_0.i2'],
  },
  {
    version: '0.51.0',
    itemKeys: ['changelog.v0_51_0.i0'],
  },
  {
    version: '0.50.0',
    itemKeys: ['changelog.v0_50_0.i0', 'changelog.v0_50_0.i1'],
  },
  {
    version: '0.49.1',
    itemKeys: ['changelog.v0_49_1.i0'],
  },
  {
    version: '0.49.0',
    itemKeys: ['changelog.v0_49_0.i0', 'changelog.v0_49_0.i1'],
  },
  {
    version: '0.48.0',
    itemKeys: ['changelog.v0_48_0.i0', 'changelog.v0_48_0.i1'],
  },
  {
    version: '0.47.2',
    itemKeys: ['changelog.v0_47_2.i0', 'changelog.v0_47_2.i1'],
  },
  {
    version: '0.47.1',
    itemKeys: ['changelog.v0_47_1.i0', 'changelog.v0_47_1.i1'],
  },
  {
    version: '0.47.0',
    itemKeys: ['changelog.v0_47_0.i0', 'changelog.v0_47_0.i1', 'changelog.v0_47_0.i2'],
  },
  {
    version: '0.46.0',
    itemKeys: ['changelog.v0_46_0.i0', 'changelog.v0_46_0.i1', 'changelog.v0_46_0.i2'],
  },
  {
    version: '0.45.0',
    itemKeys: ['changelog.v0_45_0.i0', 'changelog.v0_45_0.i1', 'changelog.v0_45_0.i2'],
  },
  {
    version: '0.44.0',
    itemKeys: ['changelog.v0_44_0.i0'],
  },
  {
    version: '0.43.0',
    itemKeys: [
      'changelog.v0_43_0.i0',
      'changelog.v0_43_0.i1',
      'changelog.v0_43_0.i2',
      'changelog.v0_43_0.i3',
    ],
  },
  {
    version: '0.42.1',
    itemKeys: ['changelog.v0_42_1.i0', 'changelog.v0_42_1.i1'],
  },
  {
    version: '0.42.0',
    itemKeys: [
      'changelog.v0_42_0.i0',
      'changelog.v0_42_0.i1',
      'changelog.v0_42_0.i2',
      'changelog.v0_42_0.i3',
    ],
  },
  {
    version: '0.41.0',
    itemKeys: ['changelog.v0_41_0.i0'],
  },
  {
    version: '0.40.0',
    itemKeys: ['changelog.v0_40_0.i0', 'changelog.v0_40_0.i1', 'changelog.v0_40_0.i2'],
  },
  {
    version: '0.39.0',
    itemKeys: ['changelog.v0_39_0.i0', 'changelog.v0_39_0.i1'],
  },
  {
    version: '0.38.0',
    itemKeys: ['changelog.v0_38_0.i0', 'changelog.v0_38_0.i1'],
  },
  {
    version: '0.37.0',
    itemKeys: ['changelog.v0_37_0.i0', 'changelog.v0_37_0.i1'],
  },
  {
    version: '0.36.0',
    itemKeys: ['changelog.v0_36_0.i0', 'changelog.v0_36_0.i1', 'changelog.v0_36_0.i2'],
  },
  {
    version: '0.35.0',
    itemKeys: ['changelog.v0_35_0.i0'],
  },
  {
    version: '0.34.0',
    itemKeys: ['changelog.v0_34_0.i0', 'changelog.v0_34_0.i1', 'changelog.v0_34_0.i2'],
  },
  {
    version: '0.33.1',
    itemKeys: ['changelog.v0_33_1.i0', 'changelog.v0_33_1.i1'],
  },
  {
    version: '0.33.0',
    itemKeys: ['changelog.v0_33_0.i0'],
  },
  {
    version: '0.32.0',
    itemKeys: ['changelog.v0_32_0.i0'],
  },
  {
    version: '0.31.0',
    itemKeys: ['changelog.v0_31_0.i0'],
  },
  {
    version: '0.30.0',
    itemKeys: ['changelog.v0_30_0.i0', 'changelog.v0_30_0.i1'],
  },
  {
    version: '0.29.0',
    itemKeys: ['changelog.v0_29_0.i0', 'changelog.v0_29_0.i1'],
  },
  {
    version: '0.28.0',
    itemKeys: ['changelog.v0_28_0.i0', 'changelog.v0_28_0.i1', 'changelog.v0_28_0.i2'],
  },
  {
    version: '0.27.0',
    itemKeys: ['changelog.v0_27_0.i0'],
  },
  {
    version: '0.26.0',
    itemKeys: ['changelog.v0_26_0.i0', 'changelog.v0_26_0.i1'],
  },
  {
    version: '0.25.0',
    itemKeys: ['changelog.v0_25_0.i0', 'changelog.v0_25_0.i1'],
  },
  {
    version: '0.24.4',
    itemKeys: ['changelog.v0_24_4.i0', 'changelog.v0_24_4.i1'],
  },
  {
    version: '0.24.3',
    itemKeys: ['changelog.v0_24_3.i0'],
  },
  {
    version: '0.24.2',
    itemKeys: ['changelog.v0_24_2.i0'],
  },
  {
    version: '0.24.1',
    itemKeys: ['changelog.v0_24_1.i0'],
  },
  {
    version: '0.24.0',
    itemKeys: ['changelog.v0_24_0.i0', 'changelog.v0_24_0.i1', 'changelog.v0_24_0.i2'],
  },
  {
    version: '0.23.0',
    itemKeys: ['changelog.v0_23_0.i0'],
  },
  {
    version: '0.22.0',
    itemKeys: [
      'changelog.v0_22_0.i0',
      'changelog.v0_22_0.i1',
      'changelog.v0_22_0.i2',
      'changelog.v0_22_0.i3',
    ],
  },
  {
    version: '0.21.0',
    itemKeys: ['changelog.v0_21_0.i0', 'changelog.v0_21_0.i1'],
  },
  {
    version: '0.20.0',
    itemKeys: [
      'changelog.v0_20_0.i0',
      'changelog.v0_20_0.i1',
      'changelog.v0_20_0.i2',
      'changelog.v0_20_0.i3',
    ],
  },
  {
    version: '0.19.0',
    itemKeys: ['changelog.v0_19_0.i0', 'changelog.v0_19_0.i1', 'changelog.v0_19_0.i2'],
  },
  {
    version: '0.18.0',
    itemKeys: ['changelog.v0_18_0.i0', 'changelog.v0_18_0.i1', 'changelog.v0_18_0.i2'],
  },
  {
    version: '0.17.0',
    itemKeys: [
      'changelog.v0_17_0.i0',
      'changelog.v0_17_0.i1',
      'changelog.v0_17_0.i2',
      'changelog.v0_17_0.i3',
    ],
  },
  {
    version: '0.16.0',
    itemKeys: [
      'changelog.v0_16_0.i0',
      'changelog.v0_16_0.i1',
      'changelog.v0_16_0.i2',
      'changelog.v0_16_0.i3',
      'changelog.v0_16_0.i4',
    ],
  },
  {
    version: '0.15.0',
    itemKeys: [
      'changelog.v0_15_0.i0',
      'changelog.v0_15_0.i1',
      'changelog.v0_15_0.i2',
      'changelog.v0_15_0.i3',
    ],
  },
  {
    version: '0.14.0',
    itemKeys: ['changelog.v0_14_0.i0'],
  },
  {
    version: '0.13.1',
    itemKeys: ['changelog.v0_13_1.i0'],
  },
  {
    version: '0.13.0',
    itemKeys: ['changelog.v0_13_0.i0'],
  },
  {
    version: '0.12.0',
    itemKeys: ['changelog.v0_12_0.i0'],
  },
  {
    version: '0.11.0',
    itemKeys: ['changelog.v0_11_0.i0'],
  },
  {
    version: '0.10.5',
    itemKeys: ['changelog.v0_10_5.i0'],
  },
  {
    version: '0.10.4',
    itemKeys: ['changelog.v0_10_4.i0'],
  },
  {
    version: '0.10.3',
    itemKeys: ['changelog.v0_10_3.i0'],
  },
  {
    version: '0.10.2',
    itemKeys: ['changelog.v0_10_2.i0'],
  },
  {
    version: '0.10.1',
    itemKeys: ['changelog.v0_10_1.i0', 'changelog.v0_10_1.i1'],
  },
  {
    version: '0.10.0',
    itemKeys: ['changelog.v0_10_0.i0', 'changelog.v0_10_0.i1', 'changelog.v0_10_0.i2'],
  },
  {
    version: '0.9.1',
    itemKeys: ['changelog.v0_9_1.i0'],
  },
  {
    version: '0.9.0',
    itemKeys: ['changelog.v0_9_0.i0', 'changelog.v0_9_0.i1', 'changelog.v0_9_0.i2'],
  },
  {
    version: '0.8.3',
    itemKeys: ['changelog.v0_8_3.i0'],
  },
  {
    version: '0.8.2',
    itemKeys: ['changelog.v0_8_2.i0', 'changelog.v0_8_2.i1'],
  },
  {
    version: '0.8.1',
    itemKeys: [
      'changelog.v0_8_1.i0',
      'changelog.v0_8_1.i1',
      'changelog.v0_8_1.i2',
      'changelog.v0_8_1.i3',
    ],
  },
  {
    version: '0.8.0',
    itemKeys: [
      'changelog.v0_8_0.i0',
      'changelog.v0_8_0.i1',
      'changelog.v0_8_0.i2',
      'changelog.v0_8_0.i3',
      'changelog.v0_8_0.i4',
    ],
  },
  {
    version: '0.7.3',
    itemKeys: ['changelog.v0_7_3.i0'],
  },
  {
    version: '0.7.2',
    itemKeys: ['changelog.v0_7_2.i0'],
  },
  {
    version: '0.7.1',
    itemKeys: ['changelog.v0_7_1.i0'],
  },
  {
    version: '0.7.0',
    itemKeys: ['changelog.v0_7_0.i0', 'changelog.v0_7_0.i1', 'changelog.v0_7_0.i2'],
  },
  {
    version: '0.6.2',
    itemKeys: ['changelog.v0_6_2.i0', 'changelog.v0_6_2.i1'],
  },
  {
    version: '0.6.1',
    itemKeys: ['changelog.v0_6_1.i0', 'changelog.v0_6_1.i1'],
  },
  {
    version: '0.6.0',
    itemKeys: [
      'changelog.v0_6_0.i0',
      'changelog.v0_6_0.i1',
      'changelog.v0_6_0.i2',
      'changelog.v0_6_0.i3',
      'changelog.v0_6_0.i4',
    ],
  },
  {
    version: '0.5.0',
    itemKeys: [
      'changelog.v0_5_0.i0',
      'changelog.v0_5_0.i1',
      'changelog.v0_5_0.i2',
      'changelog.v0_5_0.i3',
      'changelog.v0_5_0.i4',
      'changelog.v0_5_0.i5',
    ],
  },
  {
    version: '0.4.0',
    itemKeys: [
      'changelog.v0_4_0.i0',
      'changelog.v0_4_0.i1',
      'changelog.v0_4_0.i2',
      'changelog.v0_4_0.i3',
      'changelog.v0_4_0.i4',
      'changelog.v0_4_0.i5',
      'changelog.v0_4_0.i6',
    ],
  },
  {
    version: '0.3.0',
    itemKeys: [
      'changelog.v0_3_0.i0',
      'changelog.v0_3_0.i1',
      'changelog.v0_3_0.i2',
      'changelog.v0_3_0.i3',
    ],
  },
  {
    version: '0.2.0',
    itemKeys: [
      'changelog.v0_2_0.i0',
      'changelog.v0_2_0.i1',
      'changelog.v0_2_0.i2',
      'changelog.v0_2_0.i3',
      'changelog.v0_2_0.i4',
      'changelog.v0_2_0.i5',
    ],
  },
  {
    version: '0.1.0',
    itemKeys: [
      'changelog.v0_1_0.i0',
      'changelog.v0_1_0.i1',
      'changelog.v0_1_0.i2',
      'changelog.v0_1_0.i3',
    ],
  },
];
